import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useSupabaseQuery, SupabaseQueryError } from "./useSupabaseQuery";

export interface MonthlyTourScheduleItem {
  trip_id: number;
  departure_date: string;
  product_name: string;
  course_name: string;
  course_id: string;
  available_seats: number;
  max_participants: number;
  price: number;
  product_id: number;
  status: string;
}

interface BusConfiguration {
  bus_type: string;
  capacity: number;
  min_participants: number;
}

interface TripWithRelations {
  trip_id: number;
  departure_date: string;
  status: string;
  bus_type: string;
  products: {
    product_id: number;
    name: string;
    price: number;
    product_courses: Array<{
      courses: {
        course_id: string;
        name: string;
      };
    }>;
  };
}

export const useMonthlyTourSchedule = () => {
  const { supabase } = useSupabaseConnection();
  
  return useSupabaseQuery<MonthlyTourScheduleItem[]>(
    ["monthly-tour-schedule"],
    async () => {
      console.log('🗓️ 월간 투어 일정 조회 시작', {
        timestamp: new Date().toISOString(),
        queryKey: ["monthly-tour-schedule"]
      });

      // 오늘부터 30일 후까지의 예약 가능한 투어 조회
      const today = new Date().toISOString().split('T')[0];
      const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];

      // trips 조회 (current_participants 제거)
      const { data, error } = await supabase
        .from('trips')
        .select(`
          trip_id,
          departure_date,
          status,
          bus_type,
          products!inner (
            product_id,
            name,
            price,
            product_courses!inner (
              courses!inner (
                course_id,
                name
              )
            )
          )
        `)
        .in('status', ['open', 'pending', 'confirmed'])
        .gte('departure_date', today)
        .lte('departure_date', thirtyDaysLater)
        .order('departure_date', { ascending: true });

      if (error) {
        console.error('❌ 월간 투어 일정 조회 실패:', error);
        throw SupabaseQueryError.fromPostgrestError(error, 'Fetch monthly tour schedule');
      }

      if (!data || data.length === 0) {
        console.log('ℹ️ 예약 가능한 투어가 없습니다');
        return [];
      }

      console.log(`✅ 월간 투어 일정 ${data.length}개 조회 완료`);

      // 모든 trip_id 수집
      const tripIds = data.map((trip: TripWithRelations) => trip.trip_id);
      
      // RPC 함수로 실제 예약 인원 조회
      const { data: bookingCounts, error: bookingError } = await supabase
        .rpc('get_trip_booking_counts', { p_trip_ids: tripIds });
      
      if (bookingError) {
        console.error('❌ 예약 인원 조회 실패:', bookingError);
        throw SupabaseQueryError.fromPostgrestError(bookingError, 'Fetch booking counts');
      }
      
      // trip_id를 key로 하는 예약 인원 맵 생성
      const bookingCountMap: Record<number, number> = {};
      if (bookingCounts) {
        bookingCounts.forEach((item: { trip_id: number; total_participants: number }) => {
          bookingCountMap[item.trip_id] = item.total_participants;
        });
      }

      // bus_configurations 테이블 조회
      const busTypes = [...new Set(data.map((trip: TripWithRelations) => trip.bus_type).filter(Boolean))];
      let busConfigurations: BusConfiguration[] = [];
      
      if (busTypes.length > 0) {
        const { data: busConfigData, error: busConfigError } = await supabase
          .from('bus_configurations')
          .select('bus_type, capacity, min_participants')
          .in('bus_type', busTypes);
        
        if (busConfigError) {
          console.warn('⚠️ 버스 구성 정보 조회 실패:', busConfigError);
          // 버스 구성 조회 실패는 치명적이지 않으므로 계속 진행
        } else if (busConfigData) {
          busConfigurations = busConfigData;
        }
      }

      // bus_type을 key로 하는 맵 생성
      const busConfigMap = busConfigurations.reduce((acc: Record<string, BusConfiguration>, config: BusConfiguration) => {
        acc[config.bus_type] = config;
        return acc;
      }, {});

      // 데이터 변환
      const transformedData: MonthlyTourScheduleItem[] = data
        .map((trip: TripWithRelations) => {
          // RPC 함수로 조회한 실제 예약 인원 사용
          const totalBookedParticipants = bookingCountMap[trip.trip_id] || 0;
          
          // bus_configurations에서 capacity 가져오기 (기본값: 28)
          const busConfig = busConfigMap[trip.bus_type];
          const maxParticipants = busConfig?.capacity || 28;
          
          // 인솔자 포함한 총 점유 인원 계산 (예약이 있으면 +1)
          const totalOccupancy = totalBookedParticipants > 0 ? totalBookedParticipants + 1 : 0;
          
          // 실제 예약 가능한 잔여 좌석 계산
          // 예약이 없어도 인솔자 자리(3번)는 항상 예약 불가
          const availableSeats = totalBookedParticipants > 0 
            ? maxParticipants - totalOccupancy 
            : maxParticipants - 1;
          
          // 매진된 투어는 제외
          if (availableSeats <= 0) {
            return null;
          }

          // product_courses를 통해 코스 정보 가져오기
          const productCourse = trip.products?.product_courses?.[0];
          const course = productCourse?.courses;

          if (!course) {
            console.warn('⚠️ 코스 정보가 없는 투어:', trip.trip_id);
            return null;
          }

          return {
            trip_id: trip.trip_id,
            departure_date: trip.departure_date,
            product_name: trip.products.name,
            course_name: course.name,
            course_id: course.course_id,
            available_seats: availableSeats,
            max_participants: maxParticipants,
            price: Number(trip.products.price),
            product_id: trip.products.product_id,
            status: trip.status
          };
        })
        .filter(Boolean); // null 값 제거

      console.log('📊 변환된 월간 투어 데이터:', transformedData);
      return transformedData;
    },
    {
      // 타임아웃은 useSupabaseQuery에서 자동 처리됨 (기본 10초)
      timeout: 10000,
      
      // 디버깅 모드
      debug: false,
      
      // 에러 핸들러
      onError: (error) => {
        if (error.message.includes('timeout')) {
          console.warn('⏱️ 월간 투어 일정 조회 타임아웃');
        }
      },
      
      // React Query 옵션 (useSupabaseQuery의 기본값을 일부 오버라이드)
      staleTime: 5 * 60 * 1000, // 5분 동안 fresh data로 유지
      gcTime: 10 * 60 * 1000, // 10분 후 가비지 컬렉션
      refetchOnMount: false, // 마운트 시 자동 refetch 비활성화
      refetchOnWindowFocus: false, // 창 포커스 시 refetch 비활성화
      refetchOnReconnect: 'always', // 재연결 시에는 항상 refetch
      networkMode: 'online', // 온라인일 때만 실행
      enabled: true // 쿼리 항상 활성화
    }
  );
};